import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MailX, ArrowLeft, Zap, Send } from 'lucide-react';
import Header from '../component/Header';
import Footer from '../component/Footer';
import { unsubscribeUser } from '../services/subscriptionService';

const Unsubscribe = () => {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [iban, setIban] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleUnsubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedEmail = email.trim();
    const trimmedIban = iban.trim();

    if (!trimmedFirstName || !trimmedLastName || !trimmedEmail || !trimmedIban) {
      setError('Please fill in all required fields.');
      return;
    }

    if (!emailPattern.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    if (!/^\d{5}$/.test(trimmedIban)) {
      setError('Please enter the last 5 digits of your IBAN.');
      return;
    }

    setLoading(true);
    try {
      const fullName = `${trimmedFirstName} ${trimmedLastName}`.trim();

      await unsubscribeUser({
        email: trimmedEmail,
        first_name: trimmedFirstName,
        last_name: trimmedLastName,
        iban_last_5: trimmedIban,
      });
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Failed to unsubscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <main className="font-sans text-zinc-900 bg-white min-h-screen flex flex-col items-center justify-center pt-20 pb-12 px-6">
        
        {/* Background Grid Decoration */}
        

        <div className="relative z-10 w-full max-w-xl">
          {!submitted ? (
            <div className="bg-white border border-zinc-100 rounded-[2.5rem] p-8 md:p-12 shadow-[0_30px_60px_rgba(0,0,0,0.05)]">
              
              {/* Header section */}
              <div className="text-center mb-10">
                <div className="w-20 h-20 bg-amber-50 rounded-3xl flex items-center justify-center mx-auto mb-6 border border-[#f59e0b]">
                  <MailX className="w-10 h-10 text-[#f59e0b]" />
                </div>
                <h1 className="text-3xl font-black tracking-tighter text-zinc-950 mb-4">
                  Unsubscribe
                </h1>
                
                {/* Updated Text from Image */}
                <div className="mx-auto max-w-2xl text-[15px] font-medium text-zinc-600 leading-relaxed space-y-4">
                  <p>
                    To cancel your subscription, simply enter your details below. Your subscription will be cancelled immediately, and you will not be charged again.
                  </p>
                  <p>
                    If you need assistance or have any questions, feel free to contact our support team at <a href="mailto:support@learnaihub.net" className="text-amber-600 hover:text-amber-600 font-bold transition-colors hover:underline">support@learnaihub.net</a>
                  </p>
                </div>
              </div>

              <form onSubmit={handleUnsubscribe} className="space-y-6">
                {error && (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
                    {error}
                  </div>
                )}

                {/* Name Fields */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-2 ml-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="John"
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl py-4 px-6 text-sm focus:outline-none focus:border-amber-400 focus:ring-4 focus:ring-amber-400/20 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-2 ml-1">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Doe"
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl py-4 px-6 text-sm focus:outline-none focus:border-amber-400 focus:ring-4 focus:ring-amber-400/20 transition-all"
                    />
                  </div>
                </div>
                
                {/* Email Field */}
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-2 ml-1">
                    Your Email Address <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="email" 
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@example.com"
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl py-4 px-6 text-sm focus:outline-none focus:border-amber-400 focus:ring-4 focus:ring-amber-400/20 transition-all"
                  />
                </div>

                {/* IBAN Field */}
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-2 ml-1">
                    Last 5 Digits Of IBAN* <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={iban}
                    onChange={(e) => setIban(e.target.value)}
                    placeholder="12345"
                    inputMode="numeric"
                    maxLength={5}
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl py-4 px-6 text-sm focus:outline-none focus:border-amber-400 focus:ring-4 focus:ring-amber-400/20 transition-all"
                  />
                </div>

             
             

                {/* Action Button */}
                <button 
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#09090B] text-white border border-zinc-300 px-8 py-4 rounded-xl font-bold text-lg text-center hover:bg-white hover:text-zinc-950 hover:border-[#09090B] transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? 'Unsubscribing...' : (<>Unsubscribe Now <Send className="w-3.5 h-3.5" /></>)}
                </button>
              </form>

              <div className="mt-8 text-center">
                <Link 
                  to="/library" 
                  className="inline-flex items-center gap-2 font-bold text-xs text-zinc-950 transition-colors hover:underline "
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Wait, I want to keep learning
                </Link>
              </div>

              {/* Privacy Footer */}
          

            </div>
          ) : (
            /* Success State - Exactly as before */
            <div className="bg-white border border-zinc-100 rounded-[2.5rem] p-12 shadow-2xl text-center animate-in zoom-in duration-500">
              <div className="w-20 h-20 bg-zinc-50 rounded-3xl flex items-center justify-center mx-auto mb-8 border border-zinc-100">
                <Zap className="w-10 h-10 text-amber-600" />
              </div>
              <h2 className="text-3xl font-black tracking-tighter text-zinc-950 mb-4">Request Received.</h2>
              <p className="text-zinc-500 font-medium mb-10 leading-relaxed">
                You've been successfully removed from our list. We've updated our systems for <strong>{email}</strong>.
              </p>
              <Link 
                to="/" 
                className="inline-block px-10 py-4 bg-zinc-950 border border-zinc-950 text-white font-black rounded-2xl hover:bg-white hover:text-zinc-950 hover:border-zinc-950 transition-all shadow-lg text-xs uppercase tracking-widest"
              >
                Return To Home
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Unsubscribe;